import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FolderTree,
  BookOpen,
  Layers,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  FileText,
  Sparkles,
  Download,
  Upload
} from 'lucide-react'

import { resolveBackNavigation, slugify } from '@/lib/utils'
import { ImportSubjectModal } from '@/components/admin/ImportSubjectModal'



interface Standard {
  id: number
  name: string
}

interface SectionData {
  id: number
  name: string
  slug: string | null
  sort_order: number | null
  chapter_id: number
}

interface ChapterData {
  id: number
  name: string
  number: number | null
  ncert_file: string | null
  subject_id: number
  standard_ids: number[]
  standards: Standard[]
  sections: SectionData[]
}

interface SubjectDetail {
  id: number
  name: string
  code: string
  chapters: ChapterData[]
}

interface Props {
  subject: SubjectDetail
  available_standards: Standard[]
}

export default function SubjectShow({ subject, available_standards }: Props) {
  // Subject Edit Modal
  const [subjectModalOpen, setSubjectModalOpen] = useState(false)
  const [editSubjectName, setEditSubjectName] = useState(subject.name)
  const [editSubjectCode, setEditSubjectCode] = useState(subject.code)

  // Chapter Modal
  const [chapterModalOpen, setChapterModalOpen] = useState(false)
  const [editingChapterId, setEditingChapterId] = useState<number | null>(null)
  const [chapterName, setChapterName] = useState('')
  const [chapterNumber, setChapterNumber] = useState<number | ''>('')
  const [chapterNcertFile, setChapterNcertFile] = useState('')
  const [chapterStandardIds, setChapterStandardIds] = useState<number[]>([])

  // Section Modal
  const [sectionModalOpen, setSectionModalOpen] = useState(false)
  const [currentChapterIdForSection, setCurrentChapterIdForSection] = useState<number | null>(null)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
  const [sectionName, setSectionName] = useState('')
  const [sectionSlug, setSectionSlug] = useState('')
  const [sectionSortOrder, setSectionSortOrder] = useState<number | ''>('')

  // Import Modal
  const [importModalOpen, setImportModalOpen] = useState(false)


  // Summary counts
  const totalChapters = subject.chapters.length
  const totalSections = subject.chapters.reduce((acc, c) => acc + c.sections.length, 0)

  // Subject Edit Handler
  const handleSaveSubject = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editSubjectName.trim() || !editSubjectCode.trim()) return

    router.patch(`/admin/subjects/${subject.id}`, {
      subject: {
        name: editSubjectName.trim(),
        code: editSubjectCode.trim().toUpperCase(),
      }
    }, {
      onSuccess: () => setSubjectModalOpen(false)
    })
  }

  // Chapter Handlers
  const openNewChapter = () => {
    setEditingChapterId(null)
    setChapterName('')
    setChapterNumber('')
    setChapterNcertFile('')
    setChapterStandardIds([])
    setChapterModalOpen(true)
  }

  const openEditChapter = (chap: ChapterData) => {
    setEditingChapterId(chap.id)
    setChapterName(chap.name)
    setChapterNumber(chap.number ?? '')
    setChapterNcertFile(chap.ncert_file ?? '')
    setChapterStandardIds(chap.standard_ids || [])
    setChapterModalOpen(true)
  }

  const handleSaveChapter = (e: React.FormEvent) => {
    e.preventDefault()
    if (!chapterName.trim()) return

    const payload = {
      chapter: {
        name: chapterName.trim(),
        subject_id: subject.id,
        number: chapterNumber === '' ? null : chapterNumber,
        ncert_file: chapterNcertFile || null,
        standard_ids: chapterStandardIds,
      }
    }

    if (editingChapterId) {
      router.patch(`/admin/chapters/${editingChapterId}`, payload, {
        onSuccess: () => setChapterModalOpen(false)
      })
    } else {
      router.post('/admin/chapters', payload, {
        onSuccess: () => setChapterModalOpen(false)
      })
    }
  }

  const handleDeleteChapter = (chapId: number) => {
    if (!confirm('Are you sure you want to delete this chapter and its sections?')) return
    router.delete(`/admin/chapters/${chapId}`)
  }

  // Section Handlers
  const openNewSection = (chapId: number) => {
    setCurrentChapterIdForSection(chapId)
    setEditingSectionId(null)
    setSectionName('')
    setSectionSlug('')
    setSectionSortOrder('')
    setSectionModalOpen(true)
  }

  const openEditSection = (chapId: number, sec: SectionData) => {
    setCurrentChapterIdForSection(chapId)
    setEditingSectionId(sec.id)
    setSectionName(sec.name)
    setSectionSlug(sec.slug ?? '')
    setSectionSortOrder(sec.sort_order ?? '')
    setSectionModalOpen(true)
  }

  const handleSaveSection = (e: React.FormEvent) => {
    e.preventDefault()
    if (!sectionName.trim() || !currentChapterIdForSection) return

    const payload = {
      section: {
        name: sectionName.trim(),
        chapter_id: currentChapterIdForSection,
        slug: sectionSlug.trim() || null,
        sort_order: sectionSortOrder === '' ? null : sectionSortOrder,
      }
    }

    if (editingSectionId) {
      router.patch(`/admin/sections/${editingSectionId}`, payload, {
        onSuccess: () => setSectionModalOpen(false)
      })
    } else {
      router.post('/admin/sections', payload, {
        onSuccess: () => setSectionModalOpen(false)
      })
    }
  }

  const handleDeleteSection = (secId: number) => {
    if (!confirm('Are you sure you want to delete this section?')) return
    router.delete(`/admin/sections/${secId}`)
  }

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/subjects', 'Subjects')

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Top bar & Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href={backHref}>
                <ArrowLeft className="mr-1.5 h-4 w-4" /> {backLabel}
              </Link>
            </Button>
            <span className="text-muted-foreground">/</span>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{subject.name}</h1>
              <Badge variant="outline" className="font-mono text-xs uppercase bg-background font-semibold">
                {subject.code}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 font-medium"
              onClick={() => setImportModalOpen(true)}
            >
              <Upload className="h-3.5 w-3.5 text-muted-foreground" />
              Import JSON
            </Button>
            <Button variant="outline" size="sm" asChild className="gap-1.5 font-medium">
              <a href={`/admin/subjects/${subject.id}/export`}>
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
                Export JSON
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSubjectModalOpen(true)} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Edit Subject
            </Button>
            <Button size="sm" onClick={openNewChapter} className="gap-1.5 font-semibold">
              <Plus className="h-4 w-4" /> Add Chapter
            </Button>
          </div>


        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Chapters</p>
                <p className="text-2xl font-bold mt-0.5">{totalChapters}</p>
              </div>
              <FolderTree className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Sections</p>
                <p className="text-2xl font-bold mt-0.5">{totalSections}</p>
              </div>
              <Layers className="h-6 w-6 text-primary/60" />
            </CardContent>
          </Card>
        </div>

        {/* Chapters & Sections Tree */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <FolderTree className="h-5 w-5 text-primary" /> Chapter & Section Hierarchy
            </h2>
          </div>

          {subject.chapters.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <FolderTree className="h-10 w-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">No chapters defined under {subject.name} yet.</p>
                <Button size="sm" onClick={openNewChapter} className="gap-1.5">
                  <Plus className="h-4 w-4" /> Add First Chapter
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {subject.chapters.map((chap) => (
                <div
                  key={chap.id}
                  className="rounded-lg border bg-card p-4 space-y-3 transition-colors hover:border-primary/30 shadow-sm"
                >
                  {/* Chapter Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-sm flex items-center gap-1.5">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        {chap.number !== null ? `#${chap.number} ` : ''}
                        {chap.name}
                      </span>

                      {/* Standards list */}
                      {chap.standards?.map((std) => (
                        <Badge key={std.id} variant="secondary" className="text-[11px] font-semibold hover:bg-secondary/80">
                          <Link
                            href={`/admin/standards/${std.id}?return_to=${encodeURIComponent(typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/subjects/${subject.id}`)}`}
                            className="hover:underline"
                          >
                            Std: {std.name}
                          </Link>
                        </Badge>
                      ))}

                      {chap.ncert_file && (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground gap-1">
                          <FileText className="h-2.5 w-2.5" /> NCERT
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs gap-1"
                        onClick={() => openNewSection(chap.id)}
                      >
                        <Plus className="h-3 w-3" /> Add Section
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openEditChapter(chap)}
                        title="Edit Chapter"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleDeleteChapter(chap.id)}
                        title="Delete Chapter"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Sections Grid */}
                  <div className="pl-6 pt-1">
                    {chap.sections.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No sections defined yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {chap.sections.map((sec) => (
                          <div
                            key={sec.id}
                            className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 text-xs hover:bg-muted/40 transition-colors"
                          >
                            <div className="space-y-0.5 truncate pr-2">
                              <p className="font-medium truncate">{sec.name}</p>
                              {sec.slug && (
                                <p className="text-[10px] text-muted-foreground font-mono truncate">{sec.slug}</p>
                              )}
                            </div>

                            <div className="flex items-center gap-0.5 shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => openEditSection(chap.id, sec)}
                                title="Edit Section"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-destructive"
                                onClick={() => handleDeleteSection(sec.id)}
                                title="Delete Section"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 1. Modal: Edit Subject */}
        <Dialog open={subjectModalOpen} onOpenChange={setSubjectModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subject</DialogTitle>
              <DialogDescription>Update the subject name and code.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSubject} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject Name *</label>
                <Input
                  value={editSubjectName}
                  onChange={(e) => setEditSubjectName(e.target.value)}
                  placeholder="e.g. Physics"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Subject Code *</label>
                <Input
                  value={editSubjectCode}
                  onChange={(e) => setEditSubjectCode(e.target.value.toUpperCase())}
                  placeholder="e.g. PHY"
                  required
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSubjectModalOpen(false)}>Cancel</Button>
                <Button type="submit">Save Changes</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 2. Modal: Add/Edit Chapter */}
        <Dialog open={chapterModalOpen} onOpenChange={setChapterModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingChapterId ? 'Edit Chapter' : 'Add Chapter'}</DialogTitle>
              <DialogDescription>Define chapter details and applicable standard levels.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveChapter} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-2">
                  <label className="text-sm font-medium">Chapter Name *</label>
                  <Input
                    value={chapterName}
                    onChange={(e) => setChapterName(e.target.value)}
                    placeholder="e.g. Kinematics"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Chapter #</label>
                  <Input
                    type="number"
                    value={chapterNumber}
                    onChange={(e) => setChapterNumber(e.target.value ? Number(e.target.value) : '')}
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Applicable Standards</label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20">
                  {available_standards.map((std) => (
                    <label key={std.id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 rounded hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={chapterStandardIds.includes(std.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setChapterStandardIds([...chapterStandardIds, std.id])
                          } else {
                            setChapterStandardIds(chapterStandardIds.filter((id) => id !== std.id))
                          }
                        }}
                      />
                      <span>{std.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">NCERT / Resource File Reference</label>
                <Input
                  value={chapterNcertFile}
                  onChange={(e) => setChapterNcertFile(e.target.value)}
                  placeholder="e.g. physics_class11_ch2.pdf"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setChapterModalOpen(false)}>Cancel</Button>
                <Button type="submit">{editingChapterId ? 'Update Chapter' : 'Add Chapter'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 3. Modal: Add/Edit Section */}
        <Dialog open={sectionModalOpen} onOpenChange={setSectionModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSectionId ? 'Edit Section' : 'Add Section'}</DialogTitle>
              <DialogDescription>Define topic or section title and order.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveSection} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Section Name *</label>
                <Input
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  placeholder="e.g. Motion in a Straight Line"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Slug (Optional)</label>
                  <Input
                    value={sectionSlug}
                    onChange={(e) => setSectionSlug(e.target.value)}
                    placeholder="motion-in-a-straight-line"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSectionSlug(slugify(sectionName))}
                    disabled={!sectionName.trim()}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-normal"
                  >
                    <Sparkles className="h-3 w-3 text-primary" />
                    Auto-generate slug
                  </Button>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Sort Order</label>
                  <Input
                    type="number"
                    value={sectionSortOrder}
                    onChange={(e) => setSectionSortOrder(e.target.value ? Number(e.target.value) : '')}
                    placeholder="1"
                  />
                </div>
              </div>


              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSectionModalOpen(false)}>Cancel</Button>
                <Button type="submit">{editingSectionId ? 'Update Section' : 'Add Section'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 4. Modal: Import Subject */}
        <ImportSubjectModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
          targetSubjectId={subject.id}
        />
      </div>
    </AppShell>
  )
}


